<template>
  <span
    ref="triggerRef"
    class="app-tooltip-trigger"
    @mouseenter="onEnter"
    @mouseleave="onLeave"
    @focusin="onEnter"
    @focusout="onLeave"
    @click="interactive && onEnter()"
  >
    <slot />
  </span>
  <teleport to="body">
    <transition
      enter-active-class="transition ease-out duration-150"
      enter-from-class="opacity-0 translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-1"
    >
      <div
        v-if="visible && hasContent && !disabled"
        ref="bubbleRef"
        class="app-tooltip"
        :class="[`app-tooltip--${resolvedPlacement}`, `app-tooltip--${variant}`, { 'app-tooltip--interactive': interactive }]"
        :style="floatingStyle"
        role="tooltip"
        @mouseenter="interactive && onEnter()"
        @mouseleave="interactive && onLeave()"
      >
        <span class="app-tooltip-arrow" :style="arrowStyle" />
        <slot name="content">{{ content }}</slot>
      </div>
    </transition>
  </teleport>
</template>

<script>
  /**
   * AppTooltip — hover/focus tooltip wrapper.
   *
   * Centralises the legacy `Tip` (absolute-positioned arrow tooltip) and the
   * bespoke tooltip baked into `AppIconButton`. Wrap any trigger in the default
   * slot; pass `content` (or a `#content` slot) for the bubble. The bubble is
   * teleported to <body> and fixed-positioned against the trigger so it escapes
   * `overflow:hidden`/`z-index` traps — the same reason AppIconButton teleports.
   *
   * Two skins: `dark` (default) is the compact inverse-surface hint; `panel` is the
   * richer light card used by the dataset HealthStatusDot, for multi-line
   * explanations rather than a two-word label.
   *
   * Position is resolved to explicit viewport coordinates and clamped to the
   * viewport, flipping top<->bottom when the preferred side has no room. Anchoring
   * by transform alone let the bubble run off-screen for a trigger near an edge,
   * which read as the tooltip "not appearing".
   *
   * Options API (not <script setup>) so positioning logic + scroll/resize
   * listeners read clearly, mirroring AppIconButton.
   */
  import { nextTick } from 'vue';

  const VIEWPORT_MARGIN = 8;
  const ARROW_INSET = 12;

  export default {
    name: 'AppTooltip',
    props: {
      /** Tooltip text. Ignored when a `#content` slot is supplied. */
      content: { type: String, default: '' },
      /** top (default) | bottom | left | right. Top/bottom flip when there is no room. */
      placement: {
        type: String,
        default: 'top',
        validator: (v) => ['top', 'bottom', 'left', 'right'].includes(v),
      },
      /** dark (compact inverse hint) | panel (light card, for explanations). */
      variant: {
        type: String,
        default: 'dark',
        validator: (v) => ['dark', 'panel'].includes(v),
      },
      /** Hover/focus open delay in ms. Pass 0 for an explicit (i) affordance. */
      delay: { type: Number, default: 400 },
      /** When true the bubble never shows. */
      disabled: { type: Boolean, default: false },
      interactive: { type: Boolean, default: false },
    },
    data() {
      return {
        visible: false,
        hoverTimer: null,
        floatingStyle: {},
        arrowStyle: {},
        resolvedPlacement: this.placement,
      };
    },
    computed: {
      hasContent() {
        return Boolean(this.content) || Boolean(this.$slots.content);
      },
    },
    methods: {
      clamp(value, min, max) {
        if (max < min) return min;
        return Math.min(Math.max(value, min), max);
      },
      updatePosition() {
        const el = this.$refs.triggerRef;
        if (!el || typeof window === 'undefined') return;
        const rect = el.getBoundingClientRect?.();
        if (!rect) return;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        // Before the first measurement the bubble has no box yet; fall back to the CSS
        // max-width so the first frame still lands on-screen.
        const bubble = this.$refs.bubbleRef;
        const width = Math.min(
          bubble?.offsetWidth || (this.variant === 'panel' ? 304 : 256),
          Math.max(0, viewportWidth - VIEWPORT_MARGIN * 2)
        );
        const height = bubble?.offsetHeight || 0;

        if (this.placement === 'left' || this.placement === 'right') {
          this.resolvedPlacement = this.placement;
          const maxTop = Math.max(VIEWPORT_MARGIN, viewportHeight - VIEWPORT_MARGIN - height);
          this.floatingStyle = {
            left: `${
              this.placement === 'left'
                ? Math.max(VIEWPORT_MARGIN, rect.left - VIEWPORT_MARGIN - width)
                : this.clamp(
                    rect.right + VIEWPORT_MARGIN,
                    VIEWPORT_MARGIN,
                    Math.max(VIEWPORT_MARGIN, viewportWidth - VIEWPORT_MARGIN - width)
                  )
            }px`,
            top: `${this.clamp(
              rect.top + rect.height / 2 - height / 2,
              VIEWPORT_MARGIN,
              maxTop
            )}px`,
          };
          this.arrowStyle = { top: `${this.clamp(height / 2, ARROW_INSET, Math.max(ARROW_INSET, height - ARROW_INSET))}px` };
          return;
        }

        const maxLeft = Math.max(VIEWPORT_MARGIN, viewportWidth - VIEWPORT_MARGIN - width);
        const maxTop = Math.max(VIEWPORT_MARGIN, viewportHeight - VIEWPORT_MARGIN - height);
        const anchorCenterX = rect.left + rect.width / 2;
        const left = this.clamp(anchorCenterX - width / 2, VIEWPORT_MARGIN, maxLeft);

        const topIfAbove = rect.top - VIEWPORT_MARGIN - height;
        const topIfBelow = rect.bottom + VIEWPORT_MARGIN;
        const fitsAbove = height > 0 ? topIfAbove >= VIEWPORT_MARGIN : rect.top >= VIEWPORT_MARGIN * 2;
        const fitsBelow = height > 0 ? topIfBelow <= maxTop : true;
        const wantsAbove = this.placement !== 'bottom';
        this.resolvedPlacement =
          (wantsAbove && fitsAbove) || (!wantsAbove && !fitsBelow) ? 'top' : 'bottom';

        this.floatingStyle = {
          left: `${left}px`,
          top: `${this.clamp(
            this.resolvedPlacement === 'top' ? topIfAbove : topIfBelow,
            VIEWPORT_MARGIN,
            maxTop
          )}px`,
        };
        this.arrowStyle = {
          left: `${this.clamp(
            anchorCenterX - left,
            ARROW_INSET,
            Math.max(ARROW_INSET, width - ARROW_INSET)
          )}px`,
        };
      },
      onEnter() {
        if (this.disabled || !this.hasContent) return;
        clearTimeout(this.hoverTimer);
        const show = () => {
          this.visible = true;
          // Twice: once to place it from the fallback size, once from the measured box.
          nextTick(() => {
            this.updatePosition();
            nextTick(() => this.updatePosition());
          });
        };
        if (this.delay <= 0) show();
        else this.hoverTimer = setTimeout(show, this.delay);
      },
      onLeave() {
        clearTimeout(this.hoverTimer);
        if (this.interactive) this.hoverTimer = setTimeout(() => { this.visible = false; }, 150);
        else this.visible = false;
      },
      dismiss(event) {
        if (!this.interactive) return;
        const outside = event.type === 'pointerdown' &&
          !this.$refs.triggerRef?.contains(event.target) && !this.$refs.bubbleRef?.contains(event.target);
        if (event.key === 'Escape' || outside) {
          clearTimeout(this.hoverTimer);
          this.visible = false;
        }
      },
    },
    mounted() {
      window.addEventListener('scroll', this.updatePosition, true);
      window.addEventListener('resize', this.updatePosition);
      document.addEventListener('keydown', this.dismiss);
      document.addEventListener('pointerdown', this.dismiss);
    },
    beforeUnmount() {
      clearTimeout(this.hoverTimer);
      window.removeEventListener('scroll', this.updatePosition, true);
      window.removeEventListener('resize', this.updatePosition);
      document.removeEventListener('keydown', this.dismiss);
      document.removeEventListener('pointerdown', this.dismiss);
    },
  };
</script>

<style scoped>
  .app-tooltip-trigger {
    display: inline-flex;
    align-items: center;
  }

  .app-tooltip {
    position: fixed;
    z-index: 90; /* z-tooltip */
    white-space: normal;
    pointer-events: none;
  }

  .app-tooltip--interactive { pointer-events: auto; }

  .app-tooltip-arrow {
    position: absolute;
    width: 8px;
    height: 8px;
    transform: rotate(45deg) translateX(-50%);
  }

  /* --- dark: the compact inverse hint (default) --- */
  .app-tooltip--dark {
    max-width: 16rem;
    background: rgb(var(--color-surface-inverse) / 1);
    color: rgb(var(--color-text-inverse) / 1);
    font-size: 0.6875rem;
    font-weight: 500;
    line-height: 1.4;
    padding: 4px 8px;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgb(var(--color-static-black) / 0.25);
  }

  .app-tooltip--dark .app-tooltip-arrow {
    background: rgb(var(--color-surface-inverse) / 1);
  }

  /* --- panel: the dataset HealthStatusDot card, for multi-line explanations --- */
  .app-tooltip--panel {
    /* Wider and a full 13px: these bubbles carry two or three sentences of explanation, not a
       two-word label, and 12px in a 16rem column was reported as barely readable. */
    width: 19rem;
    max-width: calc(100vw - 16px);
    background: rgb(var(--color-surface-elevated) / 1);
    color: rgb(var(--color-text-primary) / 1);
    border: 1px solid rgb(var(--color-border-default) / 1);
    border-radius: var(--radius-md, 8px);
    font-size: 0.8125rem;
    line-height: 1.5;
    padding: 0.75rem;
    box-shadow: var(--shadow-dialog);
  }

  .app-tooltip--panel .app-tooltip-arrow {
    background: rgb(var(--color-surface-elevated) / 1);
    border: 1px solid rgb(var(--color-border-default) / 1);
  }

  /* The arrow hangs off whichever edge faces the trigger; the clip hides the two
     borders that would otherwise cut across the bubble's own edge. */
  .app-tooltip--top .app-tooltip-arrow {
    bottom: -4px;
    clip-path: polygon(100% 0, 100% 100%, 0 100%);
  }
  .app-tooltip--bottom .app-tooltip-arrow {
    top: -4px;
    clip-path: polygon(0 0, 100% 0, 0 100%);
  }
  .app-tooltip--left .app-tooltip-arrow {
    right: -8px;
    transform: rotate(45deg) translateY(-50%);
    clip-path: polygon(100% 0, 100% 100%, 0 0);
  }
  .app-tooltip--right .app-tooltip-arrow {
    left: 0;
    transform: rotate(45deg) translateY(-50%);
    clip-path: polygon(0 0, 0 100%, 100% 100%);
  }
</style>
